'use client';

import { Children, forwardRef, useContext, useEffect, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperStep } from './progress-stepper-step';
import { progressStepperStepsRecipe } from './styles/progress-stepper-steps.styles';
import type { ProgressStepperStepsPropsInterface } from './types/progress-stepper-steps.types';

/**
 * Renders all ProgressStepper steps. Wrapper is an ordered list so screen reader
 * users hear which step they're on (ex: "3 of 4").
 *
 * @since 10.0.0
 */
export const ProgressStepperSteps = forwardRef<
  HTMLOListElement,
  ProgressStepperStepsPropsInterface
>((props, forwardedRef) => {
  const {
    children,
    className,
    css: cssProp,
    mapStepsToProps,
    totalSteps = Children.toArray(children).length || 0,
    ...rest
  } = props;

  const {
    activeStepIndex,
    mapStepsToProps: mapStepsToPropsContext,
    orientation,
    setTotalSteps,
    steps,
  } = useContext(ProgressStepperRootContext);

  const styles = css(progressStepperStepsRecipe.raw({ orientation }), cssProp);

  useEffect(() => {
    setTotalSteps(totalSteps);
  }, [totalSteps, setTotalSteps]);

  const stepProps = useMemo(() => {
    const stepMap = mapStepsToProps || mapStepsToPropsContext;

    if (stepMap) {
      return steps.map(stepMap);
    }

    return steps.map((step) => (typeof step === 'string' ? { label: step } : step));
  }, [mapStepsToProps, mapStepsToPropsContext, steps]);

  const content = useMemo(() => {
    if (steps.length === 0) {
      return children;
    }

    return stepProps.map((step, i) => {
      return <ProgressStepperStep key={i} index={i} active={activeStepIndex === i} {...step} />;
    });
  }, [activeStepIndex, stepProps, steps, children]);

  return (
    <ol
      className={cx('c11n', styles, className)}
      ref={forwardedRef}
      data-c11n-component="ProgressStepper.Steps"
      data-c11n-version={__C11N_VERSION__}
      role="list"
      {...rest}
    >
      {content}
    </ol>
  );
});

ProgressStepperSteps.displayName = 'ProgressStepper.Steps';
